import { FC, useEffect, useRef, useState } from "react";
import styles from "./index.style.module.css";

export interface LinearProgressProps {
  /**
   * Progress value (0-100)
   */
  value: number;
  /**
   * Maximum value (default: 5)
   */
  max?: number;
  /**
   * Label to display (e.g., "Passed", "In Progress")
   */
  label?: string;
  /**
   * Label position relative to the progress bar
   */
  labelPosition?: "top" | "bottom" | "left" | "right";
  /**
   * Height of the progress bar in pixels
   */
  height?: number;
  /**
   * Width of the progress bar (default: 100%)
   */
  width?: string | number;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Start color of the gradient
   */
  gradientStartColor?: string;
  /**
   * End color of the gradient
   */
  gradientEndColor?: string;
  /**
   * Background color of the progress bar
   */
  backgroundColor?: string;
  /**
   * Border radius of the progress bar
   */
  borderRadius?: number;
  /**
   * Label text color (defaults to CSS variable)
   */
  labelColor?: string;
  /**
   * Label font size (defaults to --font-size-xs: 12px)
   */
  labelFontSize?: string;
  /**
   * Label font weight (defaults to --font-weight-normal: 400)
   */
  labelFontWeight?: string | number;
  /**
   * Enable animation on initial mount (default: true)
   */
  animateOnMount?: boolean;
  /**
   * Duration of mount animation in milliseconds (default: 1000)
   */
  animationDuration?: number;
  /**
   * When true, the progress fill has square ends (no border-radius)
   * while the track container keeps rounded corners.
   * This creates the effect where progress appears rounded at the start
   * but square where it ends (matching Figma design).
   */
  squareProgressEnd?: boolean;
}

const LinearProgress: FC<LinearProgressProps> = ({
  value,
  max = 5,
  label,
  labelPosition = "top",
  height = 8,
  width = "100%",
  className = "",
  gradientStartColor = "#a630ff",
  gradientEndColor = "#66bfff",
  backgroundColor = "#e4e4e7",
  borderRadius = 4,
  labelColor,
  labelFontSize,
  labelFontWeight,
  animateOnMount = true,
  animationDuration = 1000,
  squareProgressEnd = false,
}) => {
  const [animatedValue, setAnimatedValue] = useState(
    animateOnMount ? 0 : value,
  );
  const mountedRef = useRef(false);

  // Animate on mount and when value changes
  useEffect(() => {
    if (mountedRef.current === false) {
      // First mount - animate from 0 if enabled
      mountedRef.current = true;
      if (animateOnMount) {
        const steps = 60;
        const increment = value / steps;
        let currentStep = 0;

        const timer = setInterval(() => {
          currentStep++;
          if (currentStep >= steps) {
            setAnimatedValue(value);
            clearInterval(timer);
          } else {
            setAnimatedValue(increment * currentStep);
          }
        }, animationDuration / steps);

        return () => clearInterval(timer);
      } else {
        setAnimatedValue(value);
      }
    } else {
      // Value changed after mount - use CSS transition
      setAnimatedValue(value);
    }
  }, [value, animateOnMount, animationDuration]);

  // Calculate progress percentage
  const progressPercentage = Math.min(
    Math.max((animatedValue / max) * 100, 0),
    100,
  );

  const widthStyle = typeof width === "number" ? `${width}px` : width;

  const progressBar = (
    <div
      className={styles.progressBarContainer}
      style={{
        width: widthStyle,
        height: `${height}px`,
        backgroundColor,
        borderRadius: `${borderRadius}px`,
      }}
    >
      <div
        className={styles.progressBarFill}
        style={{
          width: `${progressPercentage}%`,
          background: `linear-gradient(90deg, ${gradientStartColor} 0%, ${gradientEndColor} 100%)`,
          ...(squareProgressEnd ? {} : { borderRadius: `${borderRadius}px` }),
        }}
      />
    </div>
  );

  const labelContent = label && (
    <div className={styles.labelContent}>
      <div
        className={styles.label}
        style={{
          ...(labelColor && { color: labelColor }),
          ...(labelFontSize && { fontSize: labelFontSize }),
          ...(labelFontWeight && { fontWeight: labelFontWeight }),
        }}
      >
        {label}
      </div>
    </div>
  );

  const getContainerClass = () => {
    switch (labelPosition) {
      case "top":
        return styles.containerColumn;
      case "bottom":
        return styles.containerColumnReverse;
      case "left":
        return styles.containerRow;
      case "right":
        return styles.containerRowReverse;
      default:
        return styles.containerColumn;
    }
  };

  return (
    <div
      className={`${styles.linearProgressWrapper} ${getContainerClass()} ${className}`}
    >
      {labelContent}
      {progressBar}
    </div>
  );
};

LinearProgress.displayName = "LinearProgress";

export default LinearProgress;
